import { TEMP_ContextBase } from "../../_common/command-descriptors";
import { SERVICES } from "@/be/services-dynamic";
import { Id } from "@/fx/Id";
import { CLM } from "../../../claims";

async function fetchClaim(id: CLM.ClaimId): Promise<CLM.Claim> {
  const { Q } = await SERVICES();

  const claim = await Q.claim({ id: id });

  return claim;
}

async function fetchUlcas(id: CLM.ClaimId): Promise<CLM.UpperLevelClaimApproval[]> {
  const { Q } = await SERVICES();

  const ulcas = await Q.ulcas({ claim: id });

  return ulcas;
}

async function fetchIssues(id: CLM.ClaimId): Promise<CLM.Issue[]> {
  const { Q } = await SERVICES();

  const issues = await Q.issues({ claim: id });

  return issues;
}

export async function fetchProductLineItems(claimId: CLM.ClaimId): Promise<CLM.ProductLineItem[]> {
  const { Q } = await SERVICES();

  const productLineItems = await Q.productLineItems({ claimId: claimId.asInt.toString() });

  return productLineItems;
}

async function fetchProduct(id: CLM.ProductId): Promise<CLM.Product> {
  const { Q } = await SERVICES();

  const product = await Q.product({ id: id.asInt.toString() });

  return product;
}

export async function claimContextProvider<TContext extends TEMP_ContextBase>(result: bigint): Promise<TContext> {
  const claimId = Id.from(result) as CLM.ClaimId;
  const claim   = await fetchClaim(claimId);

  return { data: { claim }, ids: { claimId } } as unknown as TContext;
}

export async function claimAndUlcaContextProvider<TContext extends TEMP_ContextBase>(result: bigint): Promise<TContext> {
  const claimId = Id.from(result) as CLM.ClaimId;
  const claim   = await fetchClaim(claimId);
  const ulcas   = await fetchUlcas(claimId);
  const ulcaId  = ulcas.length > 0 ? ulcas[ulcas.length - 1].id : undefined;

  return { data: { claim, ulcas }, ids: { claimId, ulcaId } } as unknown as TContext;
}

export async function claimAndIssueContextProvider<TContext extends TEMP_ContextBase>(result: bigint): Promise<TContext> {
  const claimId = Id.from(result) as CLM.ClaimId;
  const claim   = await fetchClaim(claimId);
  const issues  = await fetchIssues(claimId);
  const issueId = issues.length > 0 ? issues[issues.length - 1].id : undefined;

  return { data: { claim, issues }, ids: { claimId, issueId } } as unknown as TContext;
}

export async function claimAndProductLineItemContextProvider<TContext extends TEMP_ContextBase>(result: bigint): Promise<TContext> {
  const claimId          = Id.from(result) as CLM.ClaimId;
  const claim            = await fetchClaim(claimId);
  const productLineItems = await fetchProductLineItems(claimId);

  return { data: { claim, productLineItems }, ids: { claimId } } as unknown as TContext;
}

export async function productContextProvider<TContext extends TEMP_ContextBase>(result: bigint): Promise<TContext> {
  const productId = Id.from(result) as CLM.ProductId;
  const product   = await fetchProduct(productId);

  return { data: { product }, ids: { productId } } as unknown as TContext;
}
